import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

// material-ui
import { alpha, useTheme } from '@mui/material/styles';
import { Badge, Box, Paper, Portal, Stack, Tooltip, Typography } from '@mui/material';

// ativos
import { WechatOutlined, CloseOutlined, MinusOutlined, ExpandAltOutlined } from '@ant-design/icons';

// importação do projeto
import ListaConversas from './ListaConversas';
import Conversa from './Conversa';
import IconButton from '../../../../../components/@extended/IconButton';
import { ThemeMode } from '../../../../../config';
import { conversas as conversasMock, membrosEquipe, respostasSimuladas } from './mockChat';

// ==============================|| CABEÇALHO DE CONTEÚDO - CHAT DA EQUIPE ||============================== //

// Janela flutuante ancorada no canto inferior direito: continua aberta enquanto o
// usuário navega pela plataforma, pode ser redimensionada pelas bordas superior/esquerda
// e minimizada para uma barra compacta.
const LARGURA_PADRAO = 880;
const ALTURA_PADRAO = 600;
const LARGURA_MIN = 380;
const ALTURA_MIN = 420;
const LARGURA_MINIMIZADO = 320;
// Abaixo desta largura o painel não tem espaço para as duas colunas.
const LIMITE_DUAS_COLUNAS = 720;
const CHAVE_ARMAZENAMENTO = 'egrc_chat_painel';

const horaAgora = () => new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

const lerPreferencias = () => {
  try {
    const salvo = JSON.parse(localStorage.getItem(CHAVE_ARMAZENAMENTO) || '{}');
    return {
      largura: Number(salvo.largura) || LARGURA_PADRAO,
      altura: Number(salvo.altura) || ALTURA_PADRAO,
      minimizado: Boolean(salvo.minimizado)
    };
  } catch {
    return { largura: LARGURA_PADRAO, altura: ALTURA_PADRAO, minimizado: false };
  }
};

const Chat = () => {
  const theme = useTheme();
  const modoEscuro = theme.palette.mode === ThemeMode.DARK;

  const temporizadores = useRef([]);
  const preferencias = useRef(lerPreferencias());

  const [aberto, setAberto] = useState(false);
  const [minimizado, setMinimizado] = useState(preferencias.current.minimizado);
  const [tamanho, setTamanho] = useState({ largura: preferencias.current.largura, altura: preferencias.current.altura });
  const [viewport, setViewport] = useState({ largura: window.innerWidth, altura: window.innerHeight });
  const [tooltipAberto, setTooltipAberto] = useState(false);

  const [conversas, setConversas] = useState(conversasMock);
  const [idSelecionado, setIdSelecionado] = useState(conversasMock[0]?.id ?? null);
  const [digitandoEm, setDigitandoEm] = useState(null);
  // Em painel estreito alterna entre a lista e a conversa (uma coluna por vez).
  const [mostrandoConversa, setMostrandoConversa] = useState(false);

  // Limpa os temporizadores da simulação ao desmontar.
  useEffect(
    () => () => {
      temporizadores.current.forEach(clearTimeout);
      temporizadores.current = [];
    },
    []
  );

  // Mantém o painel dentro da janela quando ela é redimensionada.
  useEffect(() => {
    const aoRedimensionar = () => setViewport({ largura: window.innerWidth, altura: window.innerHeight });
    window.addEventListener('resize', aoRedimensionar);
    return () => window.removeEventListener('resize', aoRedimensionar);
  }, []);

  // Persiste tamanho e estado minimizado escolhidos pelo usuário.
  useEffect(() => {
    try {
      localStorage.setItem(CHAVE_ARMAZENAMENTO, JSON.stringify({ ...tamanho, minimizado }));
    } catch {
      /* localStorage indisponível: o painel só perde a preferência de tamanho */
    }
  }, [tamanho, minimizado]);

  const agendar = (fn, ms) => {
    temporizadores.current.push(setTimeout(fn, ms));
  };

  const naoLidasTotal = useMemo(() => conversas.reduce((total, c) => total + c.naoLidas, 0), [conversas]);

  const conversaAtiva = conversas.find((c) => c.id === idSelecionado);
  const membroAtivo = membrosEquipe.find((m) => m.id === conversaAtiva?.idMembro);
  const qtdOnline = membrosEquipe.filter((m) => m.status === 'online').length;

  const marcarComoLida = useCallback((id) => {
    setConversas((atuais) => atuais.map((c) => (c.id === id ? { ...c, naoLidas: 0 } : c)));
  }, []);

  // Limites reais, já considerando a janela do navegador.
  const larguraMax = Math.max(LARGURA_MIN, viewport.largura - 32);
  const alturaMax = Math.max(ALTURA_MIN, viewport.altura - 114);
  const largura = Math.min(tamanho.largura, larguraMax);
  const altura = Math.min(tamanho.altura, alturaMax);
  const colunaUnica = largura < LIMITE_DUAS_COLUNAS;

  const alternarPainel = () => {
    setTooltipAberto(false);
    if (!aberto) {
      setAberto(true);
      setMinimizado(false);
      if (idSelecionado) marcarComoLida(idSelecionado);
      return;
    }
    if (minimizado) {
      setMinimizado(false);
      if (idSelecionado) marcarComoLida(idSelecionado);
      return;
    }
    setAberto(false);
  };

  const fechar = () => {
    setAberto(false);
    setMostrandoConversa(false);
  };

  const selecionarConversa = (id) => {
    setIdSelecionado(id);
    marcarComoLida(id);
    setMostrandoConversa(true);
  };

  // Redimensionamento pelas bordas superior e esquerda (o painel está fixo no canto inferior direito).
  const iniciarRedimensionamento = (eixo) => (evento) => {
    evento.preventDefault();
    const inicio = { x: evento.clientX, y: evento.clientY, largura, altura };

    const mover = (ev) => {
      setTamanho({
        largura:
          eixo === 'y' ? inicio.largura : Math.min(larguraMax, Math.max(LARGURA_MIN, inicio.largura + (inicio.x - ev.clientX))),
        altura: eixo === 'x' ? inicio.altura : Math.min(alturaMax, Math.max(ALTURA_MIN, inicio.altura + (inicio.y - ev.clientY)))
      });
    };

    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      document.body.style.userSelect = '';
    };

    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };

  // Envio simulado: mensagem entra como "enviada", evolui para entregue/lida
  // e o contato responde. Sem backend — apenas para avaliação visual.
  const enviarMensagem = (texto) => {
    const idConversa = idSelecionado;
    const idMensagem = Date.now();

    setConversas((atuais) =>
      atuais.map((c) =>
        c.id === idConversa
          ? {
              ...c,
              mensagens: [...c.mensagens, { id: idMensagem, remetente: 'eu', texto, hora: horaAgora(), dia: 'Hoje', situacao: 'enviada' }]
            }
          : c
      )
    );

    const atualizarSituacao = (situacao) =>
      setConversas((atuais) =>
        atuais.map((c) =>
          c.id === idConversa ? { ...c, mensagens: c.mensagens.map((m) => (m.id === idMensagem ? { ...m, situacao } : m)) } : c
        )
      );

    agendar(() => atualizarSituacao('entregue'), 600);
    agendar(() => atualizarSituacao('lida'), 1600);
    agendar(() => setDigitandoEm(idConversa), 1900);
    agendar(() => {
      setDigitandoEm(null);
      const resposta = respostasSimuladas[Math.floor(Math.random() * respostasSimuladas.length)];
      setConversas((atuais) =>
        atuais.map((c) =>
          c.id === idConversa
            ? { ...c, mensagens: [...c.mensagens, { id: idMensagem + 1, remetente: 'contato', texto: resposta, hora: horaAgora(), dia: 'Hoje' }] }
            : c
        )
      );
    }, 3600);
  };

  const iconBackColorOpen = modoEscuro ? 'background.default' : 'grey.100';
  const exibirLista = !colunaUnica || !mostrandoConversa;
  const exibirConversa = (!colunaUnica || mostrandoConversa) && conversaAtiva && membroAtivo;

  // Alças de redimensionamento (bordas superior e esquerda + canto).
  const alcaBase = { position: 'absolute', zIndex: 2, '&:hover': { bgcolor: alpha(theme.palette.common.white, 0.25) } };

  return (
    <>
      <Box sx={{ flexShrink: 0, ml: 0.75 }}>
        <Tooltip
          title="Chat da equipe"
          open={tooltipAberto}
          onOpen={() => !aberto && setTooltipAberto(true)}
          onClose={() => setTooltipAberto(false)}
        >
          <IconButton
            color="secondary"
            variant="light"
            sx={{ color: 'text.primary', bgcolor: aberto ? iconBackColorOpen : 'transparent' }}
            aria-label="abrir chat da equipe"
            aria-expanded={aberto}
            onClick={alternarPainel}
          >
            <Badge badgeContent={naoLidasTotal} color="error">
              <WechatOutlined />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>

      {aberto && (
        <Portal>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              right: 16,
              bottom: 34, // acima do badge de versão do app (fixed, z-index 2000)
              zIndex: 1250,
              width: minimizado ? LARGURA_MINIMIZADO : largura,
              height: minimizado ? 'auto' : altura,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderRadius: 1.5,
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: theme.customShadows?.z1
            }}
          >
            {!minimizado && (
              <>
                <Box
                  onPointerDown={iniciarRedimensionamento('y')}
                  sx={{ ...alcaBase, top: 0, left: 12, right: 0, height: 6, cursor: 'ns-resize' }}
                />
                <Box
                  onPointerDown={iniciarRedimensionamento('x')}
                  sx={{ ...alcaBase, top: 12, left: 0, bottom: 0, width: 6, cursor: 'ew-resize' }}
                />
                <Box
                  onPointerDown={iniciarRedimensionamento('xy')}
                  aria-label="redimensionar painel do chat"
                  sx={{ ...alcaBase, top: 0, left: 0, width: 14, height: 14, cursor: 'nwse-resize' }}
                />
              </>
            )}

            {/* Barra de título (mesmo padrão do drawer de Customização do Sistema) */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ px: 1.5, py: 1, pl: 2, bgcolor: 'primary.main', color: 'background.paper', flexShrink: 0 }}
            >
              <Typography variant="subtitle1" sx={{ flexGrow: 1, minWidth: 0 }} noWrap>
                Chat da equipe
              </Typography>
              {minimizado ? (
                naoLidasTotal > 0 && (
                  <Box
                    sx={{
                      minWidth: 20,
                      height: 20,
                      px: 0.625,
                      borderRadius: 10,
                      bgcolor: 'error.main',
                      color: 'common.white',
                      fontSize: '0.6875rem',
                      fontWeight: 600,
                      lineHeight: '20px',
                      textAlign: 'center'
                    }}
                  >
                    {naoLidasTotal}
                  </Box>
                )
              ) : (
                <Typography variant="caption" sx={{ opacity: 0.85, flexShrink: 0 }}>
                  {qtdOnline} online
                </Typography>
              )}
              <IconButton
                shape="rounded"
                size="small"
                sx={{ color: 'background.paper' }}
                onClick={() => setMinimizado((m) => !m)}
                aria-label={minimizado ? 'restaurar painel do chat' : 'minimizar painel do chat'}
              >
                {minimizado ? <ExpandAltOutlined style={{ fontSize: '0.95rem' }} /> : <MinusOutlined style={{ fontSize: '0.95rem' }} />}
              </IconButton>
              <IconButton
                shape="rounded"
                size="small"
                sx={{ color: 'background.paper' }}
                onClick={fechar}
                aria-label="fechar painel do chat"
              >
                <CloseOutlined style={{ fontSize: '0.95rem' }} />
              </IconButton>
            </Stack>

            {!minimizado && (
              <Stack direction="row" sx={{ flexGrow: 1, minHeight: 0 }}>
                {exibirLista && (
                  <ListaConversas
                    conversas={conversas}
                    idSelecionado={idSelecionado}
                    onSelecionar={selecionarConversa}
                    larguraCheia={!exibirConversa}
                  />
                )}
                {exibirConversa && (
                  <Conversa
                    membro={membroAtivo}
                    mensagens={conversaAtiva.mensagens}
                    digitando={digitandoEm === conversaAtiva.id}
                    onEnviar={enviarMensagem}
                    onVoltar={colunaUnica ? () => setMostrandoConversa(false) : undefined}
                  />
                )}
              </Stack>
            )}
          </Paper>
        </Portal>
      )}
    </>
  );
};

export default Chat;
